import { Component } from 'react';
import { connect } from 'react-redux';
import { actions } from 'forms/rrf';
import { Row, Col } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import ArrowButton from '../../../common/button-arrow';
import GameLevelFormColumn from './game-level-form-column';
import { clearIncentiveErrors } from '../../../../actions/incentives';
import { isGameLevelKey, serverErrorMessages } from '../../../../utils/serverErrors';

// The three controls a level is made of. All are required.
const LEVEL_FIELDS = ['minimum-number-of-scans', 'prize-amount', 'cataboom-id'];

class GameLevelForm extends Component {
  handleAddColumn = () => {
    const gameLevels = this.gameLevels();
    const level = gameLevels.filter(gameLevel => !gameLevel._destroy).length + 1;
    const { incentive, dispatch } = this.props;
    const newGameLevels = cloneDeep(incentive.game_levels);
    if (level > Object.keys(newGameLevels).length) {
      // These three keys are the ones the column's controls are bound to, and the ones the server
      // sends back for a saved level. A level starts empty rather than on zero: all three are
      // required, and a zero sitting in the box looks filled in while the form counts it as blank.
      newGameLevels[`level-${level}`] = {};
      newGameLevels[`level-${level}`]['minimum-number-of-scans'] = { value: '' };
      newGameLevels[`level-${level}`]['prize-amount'] = { value: '' };
      newGameLevels[`level-${level}`]['cataboom-id'] = { value: '' };
    } else {
      newGameLevels[`level-${level}`]._destroy = false;
    }

    dispatch(actions.change('deep.incentives.game_levels', newGameLevels));
    dispatch(clearIncentiveErrors());
    this.showLevelRequirements(level);
  }

  // A required control only renders its error once it has been touched, so a level that is added
  // and not filled in takes the Save button out with nothing on screen to say why - and Game link
  // is the easy one to miss, because nothing about a blank box says it is holding up the save.
  // Touching the new level's controls puts "This field is required" under whichever are still
  // blank. A control that is valid shows nothing, so this only ever surfaces real gaps.
  showLevelRequirements = (level) => {
    const { dispatch } = this.props;

    LEVEL_FIELDS.forEach((field) => {
      dispatch(actions.setTouched(`deep.incentives.game_levels.level-${level}.${field}.value`));
    });
  }

  handleRemoveColumn = () => {
    const gameLevels = this.gameLevels();
    if (gameLevels.filter(gameLevel => !gameLevel._destroy).length > 0) {
      const level = gameLevels.filter(gameLevel => !gameLevel._destroy).length;
      const { incentive, dispatch } = this.props;
      const newGameLevels = cloneDeep(incentive.game_levels);

      if (newGameLevels[`level-${level}`].id) {
        // eslint-disable-next-line dot-notation
        newGameLevels[`level-${level}`]['_destroy'] = true;
      } else {
        delete newGameLevels[`level-${level}`];
      }

      dispatch(actions.change('deep.incentives.game_levels', newGameLevels));
      dispatch(clearIncentiveErrors());
    }
  }

  isLast = (index) => {
    if (index + 1 === 1) {
      return false;
    }

    return this.gameLevels().length === index + 1;
  }

  gameLevels = () => {
    const { incentive } = this.props;

    if (incentive && incentive.game_levels) {
      return Object.values(incentive.game_levels);
    }

    return [];
  }

  // The server rejects a game with no levels, with two levels on the same rung and with two
  // levels sharing a scan target, and it validates each level in its own right. A complaint about
  // the ladder as a whole comes back under "game_levels", one raised by a level itself under a
  // dotted key ("game_levels.minimum_number_of_scans"). Both are shown here, next to the levels
  // they are about; everything else the server objected to is shown at the top of the form.
  gameLevelErrors = () => serverErrorMessages(this.props.errors, isGameLevelKey)

  render() {
    const gameLevels = this.gameLevels();
    const gameLevelErrors = this.gameLevelErrors();
    // eslint-disable-next-line dot-notation

    return (
      <div className="container-fluid">
        {gameLevelErrors.length !== 0 && (
          <Row>
            <Col md={12}>
              <div className="errors text-danger">
                {gameLevelErrors.map(error => (
                  <div key={error}>{error}</div>
                ))}
              </div>
            </Col>
          </Row>
        )}
        <Row>
          {gameLevels.length !== 0 &&
            gameLevels
              // eslint-disable-next-line dot-notation
              .filter(gameLevel => !gameLevel['_destroy'])
              .map((gameLevel, index) => (
                <GameLevelFormColumn
                  key={index}
                  level={index + 1}
                  isLast={this.isLast(index)}
                />
              ))}
        </Row>
        <Row>
          <Col md={2}>
            <ArrowButton
              arrow={false}
              submit={false}
              color="red"
              onClick={this.handleAddColumn}
            >
              Add game level
            </ArrowButton>
          </Col>
          <Col md={2}>
            <ArrowButton
              arrow={false}
              submit={false}
              color='red'
              onClick={this.handleRemoveColumn}
              disabled={gameLevels.length === 0}
            >
              Remove game level
            </ArrowButton>
          </Col>
          <Col md={8}></Col>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  incentive: state.deep.incentives,
  errors: state.incentives.errors
});

export default connect(mapStateToProps)(GameLevelForm);
